<template>
  <div v-if="exploded" class="flex size-10 cursor-pointer items-center justify-center rounded-full">
    <div
      class="absolute rounded-full border border-dashed border-black transition-all duration-300"
      :style="{
        width: `${radius * 2}px`,
        height: `${radius * 2}px`,
      }"
    ></div>

    <div
      v-for="(hazard, index) in sortedClusterHazards"
      :key="hazard.id"
      class="absolute size-10 transition-all duration-300"
      :style="{
        transform: `rotate(${angle * index}rad)`,
      }"
    >
      <div
        class="absolute size-10 cursor-pointer transition-all duration-300"
        :style="{
          top: `-${radius}px`,
          transform: `rotate(-${angle * index}rad)`,
        }"
      >
        <ui-map-marker :feature="hazard" :marker="marker" />
      </div>
    </div>
    <div class="absolute left-2 top-2 transition-all">
      <control @click.stop="closeCluster" />
    </div>
  </div>
  <div v-else class="size-10 transition-opacity duration-300" :class="{ 'opacity-40': isDimmed }">
    <gv-image
      class="absolute top-0 size-10 cursor-pointer transition-all"
      :src="clusterIcon"
      alt="cluster marker"
      @click.stop="onClick"
    ></gv-image>

    <button class="absolute top-0 flex size-4 cursor-pointer items-center justify-center rounded-full bg-indigo-450" @click.stop="onClick">
      <div class="mt-[2px] font-proxima-nova text-2xs font-bold text-white">
        {{ badgeNumber }}
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { HAZARD_MARKERS, isInSelectedAnnouncement, useHazardsStore, useMapStore } from '@/features/hazards';
import { bbox } from '@turf/turf';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { LngLatBoundsLike, LngLatLike, Marker } from 'mapbox-gl';
import { computed, onMounted, ref } from 'vue';

import Control from './control.svg?component';

const map = useMapStore();
const hazardsStore = useHazardsStore();

const props = defineProps<{
  refresh?: number;
  coords: LngLatLike;
  marker: Marker;
  sortedClusterHazards: Feature<Geometry, GeoJsonProperties>[];
  clusterLeaves: Feature<Geometry, GeoJsonProperties>[];
}>();

const clusterIcon = computed(() => HAZARD_MARKERS[props.sortedClusterHazards[0].properties?.['icon-name'] as keyof typeof HAZARD_MARKERS]);

const badgeNumber = computed(() => props.clusterLeaves.length);

/**
 * A collapsed cluster is only dimmed when none of its hazards belong to the selected announcement.
 * When exploded, each {@link UiMapMarker} handles its own dimming.
 */
const isDimmed = computed(() =>
  props.clusterLeaves.every((leaf) => !isInSelectedAnnouncement(leaf.properties, hazardsStore.selectedAnnouncement))
);

const exploded = ref(false);
const radius = ref<number>(0);
const angle = ref(0);
const emit = defineEmits<{ exploded: [boolean] }>();

onMounted(() => {
  // these can get re-used - always start collapsed
  radius.value = 0;
  angle.value = 0;
  exploded.value = false;
  map.mapbox?.on('zoomend', () => {
    if (radius.value === 0) return;
    const zoom = map.mapbox?.getZoom() || 0;

    if (zoom < 11) closeCluster();
  });
});

const onClick = async (e: MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
  const zoom = map.mapbox?.getZoom() || 0;

  if (zoom >= 14) return explodeCluster();

  zoomCluster();
};

const closeCluster = () => {
  radius.value = 0;
  angle.value = 0;
  // allow time for transition
  setTimeout(() => (exploded.value = false), 300);
};

const calculateExplosion = () => {
  if (props.sortedClusterHazards.length <= 1) angle.value = 0;
  else angle.value = (2 * Math.PI) / props.sortedClusterHazards.length;

  if (props.sortedClusterHazards.length <= 3) radius.value = 30;
  else radius.value = Math.max(30, Math.abs(40 / Math.sin(angle.value)));
};

const explodeCluster = () => {
  // prepare for transition to explosion
  radius.value = 0;
  angle.value = 0;
  exploded.value = true;
  emit('exploded', exploded.value);
  setTimeout(calculateExplosion, 0);
};

const zoomCluster = async () => {
  const bounds = bbox({
    type: 'FeatureCollection',
    features: props.clusterLeaves,
  });

  map.mapbox?.fitBounds(bounds as LngLatBoundsLike, {
    maxZoom: 15,
    padding: { top: 20, bottom: 90, left: 20, right: 80 },
    speed: 5,
  });
};
</script>
